import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCommunitiesApi, getCommunityByIdApi } from '../../api/communities';

// ──────────────────────────────────────────────
// Async Thunks
// ──────────────────────────────────────────────

export const fetchCommunities = createAsyncThunk(
  'community/fetchCommunities',
  async ({ search = '', type = '', category = '' } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (search) params.search = search;
      if (type) params.type = type;
      if (category) params.category = category;
      return await getCommunitiesApi(params);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch communities' });
    }
  }
);

export const fetchCommunityById = createAsyncThunk(
  'community/fetchCommunityById',
  async (id, { rejectWithValue }) => {
    try {
      return await getCommunityByIdApi(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch community' });
    }
  }
);

// ──────────────────────────────────────────────
// Slice
// ──────────────────────────────────────────────

const communitySlice = createSlice({
  name: 'community',
  initialState: {
    communities: [],
    currentCommunity: null,
    pagination: {
      total: 0,
      page: 1,
      limit: 20,
      pages: 0,
    },
    loading: false,
    error: null,
    filters: {
      search: '',
      type: '',
      selectedCategory: '',
    },
  },
  reducers: {
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
    },
    setTypeFilter: (state, action) => {
      state.filters.type = action.payload;
    },
    setSelectedCategory: (state, action) => {
      state.filters.selectedCategory = action.payload;
    },
    clearFilters: (state) => {
      state.filters = { search: '', type: '', selectedCategory: '' };
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchCommunities ──
      .addCase(fetchCommunities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCommunities.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        // Handle multiple API response shapes:
        // { data: [...] }  OR  { data: { communities: [...], pagination: {} } }
        if (Array.isArray(payload?.data)) {
          state.communities = payload.data;
          state.pagination = payload.pagination || state.pagination;
        } else if (Array.isArray(payload?.data?.communities)) {
          state.communities = payload.data.communities;
          state.pagination = payload.data.pagination || state.pagination;
        } else if (Array.isArray(payload)) {
          state.communities = payload;
        } else {
          state.communities = [];
        }
      })
      .addCase(fetchCommunities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch communities';
      })
      // ── fetchCommunityById ──
      .addCase(fetchCommunityById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCommunityById.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.currentCommunity = payload?.data?.community || payload?.data || null;
      })
      .addCase(fetchCommunityById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch community';
      });
  },
});

export const {
  setSearchFilter,
  setTypeFilter,
  setSelectedCategory,
  clearFilters,
  clearError,
} = communitySlice.actions;

export default communitySlice.reducer;
